"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Background = void 0;
const pubsub_js_1 = require("pubsub-js");
const three_1 = require("three");
const config_1 = require("../../config");
const utils_1 = require("../../utils/utils");
const BackgroundControls_1 = require("./components/BackgroundControls");
const MAX_SAVED_COLORS = 5;
class Background {
    constructor(webgl) {
        this.debouncedCreateColorBlock = (0, utils_1.debounce)(this.createNewColorBlock.bind(this), 2000);
        this.backgroundFunctions = {
            typeChange: () => this.typeChange(),
            colorChange: () => this.change(),
            openColorPicker: () => this.openColorPicker(),
            imageA: () => this.change(config_1.config.resources.images.background[0].name),
            imageB: () => this.change(config_1.config.resources.images.background[1].name)
        };
        this.webgl = webgl;
        const parentEl = document.getElementById('background');
        if (!parentEl) {
            throw new Error("Parent element (background) is undefined");
        }
        this.backgroundControls = new BackgroundControls_1.BackgroundControls(parentEl);
        this.addEvents();
        (0, pubsub_js_1.subscribe)("initial-background-set" /* INITIAL_BACKGROUND_SET */, (_, color) => this.setInitialColor(color));
    }
    setInitialColor(color) {
        var _a, _b;
        (_b = (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.colorPicker) === null || _b === void 0 ? void 0 : _b.setValue(color);
        this.onBackgroundChanged();
    }
    dispose() {
        var _a;
        this.removeEvents();
        (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.dispose();
        delete this.backgroundControls;
    }
    getData() {
        var _a, _b, _c, _d, _e;
        return {
            type: (_b = (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.backgroundType) === null || _b === void 0 ? void 0 : _b.getValue(),
            color: (_d = (_c = this.backgroundControls) === null || _c === void 0 ? void 0 : _c.colorPicker) === null || _d === void 0 ? void 0 : _d.getValue(),
            texture: (_e = this.backgroundControls) === null || _e === void 0 ? void 0 : _e.selectedImage
        };
    }
    setInitialData(data) {
        var _a, _b, _c, _d;
        (_b = (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.backgroundType) === null || _b === void 0 ? void 0 : _b.setValue(data.type);
        (_d = (_c = this.backgroundControls) === null || _c === void 0 ? void 0 : _c.colorPicker) === null || _d === void 0 ? void 0 : _d.setValue(data.color);
        this.backgroundControls.selectedImage = data.texture;
        this.change();
    }
    makeButtonSelected(button) {
        var _a, _b;
        if (button.classList.contains('selected')) {
            return;
        }
        const currentSelected = document.querySelector('.color-block.selected');
        currentSelected === null || currentSelected === void 0 ? void 0 : currentSelected.classList.remove('selected');
        button.classList.add('selected');
        if (button === null || button === void 0 ? void 0 : button.dataset.color) {
            (_b = (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.colorPicker) === null || _b === void 0 ? void 0 : _b.setValue(button.dataset.color);
            this.setColorAsBackground(button.dataset.color);
        }
    }
    addEvents() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        (_b = (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.backgroundType) === null || _b === void 0 ? void 0 : _b.setEvent('change', this.backgroundFunctions.typeChange);
        (_d = (_c = this.backgroundControls) === null || _c === void 0 ? void 0 : _c.colorPicker) === null || _d === void 0 ? void 0 : _d.setEvent('input', this.backgroundFunctions.colorChange);
        (_f = (_e = this.backgroundControls) === null || _e === void 0 ? void 0 : _e.backgroundImageA) === null || _f === void 0 ? void 0 : _f.setEvent('click', this.backgroundFunctions.imageA);
        (_h = (_g = this.backgroundControls) === null || _g === void 0 ? void 0 : _g.backgroundImageB) === null || _h === void 0 ? void 0 : _h.setEvent('click', this.backgroundFunctions.imageB);
        (_k = (_j = this.backgroundControls) === null || _j === void 0 ? void 0 : _j.colorPickerBtn) === null || _k === void 0 ? void 0 : _k.addEventListener('click', this.backgroundFunctions.openColorPicker);
    }
    removeEvents() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        (_b = (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.backgroundType) === null || _b === void 0 ? void 0 : _b.removeEvent('change', this.backgroundFunctions.typeChange);
        (_d = (_c = this.backgroundControls) === null || _c === void 0 ? void 0 : _c.colorPicker) === null || _d === void 0 ? void 0 : _d.removeEvent('input', this.backgroundFunctions.colorChange);
        (_f = (_e = this.backgroundControls) === null || _e === void 0 ? void 0 : _e.backgroundImageA) === null || _f === void 0 ? void 0 : _f.removeEvent('click', this.backgroundFunctions.imageA);
        (_h = (_g = this.backgroundControls) === null || _g === void 0 ? void 0 : _g.backgroundImageB) === null || _h === void 0 ? void 0 : _h.removeEvent('click', this.backgroundFunctions.imageB);
    }
    typeChange() {
        var _a;
        const backgroundData = this.getData();
        if (backgroundData.type === "TEXTURE" /* TEXTURE */) {
            this.setTextureAsBackground(backgroundData.texture);
        }
        else if (backgroundData.type === "COLOR" /* COLOR */) {
            this.setColorAsBackground(backgroundData.color);
            // Ugly. Temporary fix until rewrite.
            const buttons = document.getElementsByClassName('color-block');
            if (!buttons.length) {
                this.createNewColorBlock();
            }
        }
        (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.refreshBackgroundUI();
    }
    change(imageName) {
        var _a;
        this.onBackgroundChanged(imageName);
        (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.refreshBackgroundUI();
    }
    openColorPicker() {
        var _a, _b;
        (_b = (_a = this.backgroundControls) === null || _a === void 0 ? void 0 : _a.colorPicker) === null || _b === void 0 ? void 0 : _b.domEl.click();
    }
    onBackgroundChanged(imageName) {
        if (imageName != undefined) {
            this.backgroundControls.selectedImage = imageName;
        }
        const data = this.getData();
        switch (data.type) {
            case "UNSPECIFIED" /* UNSPECIFIED */:
                this.setColorAsBackground(null);
                break;
            case "COLOR" /* COLOR */:
                this.setColorAsBackground(data.color);
                this.debouncedCreateColorBlock();
                break;
            case "TEXTURE" /* TEXTURE */:
                if (data.texture) {
                    new three_1.TextureLoader().load(data.texture, (texture) => {
                        this.webgl.scene.background = texture;
                        this.webgl.renderOnce();
                    });
                }
                break;
            default: throw new Error("Unknown background type: " + data.type);
        }
    }
    createNewColorBlock() {
        const { color } = this.getData();
        const buttons = document.getElementsByClassName('color-block');
        const currentSelected = document.querySelector('.color-block.selected');
        currentSelected === null || currentSelected === void 0 ? void 0 : currentSelected.classList.remove('selected');
        const btn = document.createElement('button');
        btn.classList.add('color-block');
        btn.classList.add('selected');
        btn.setAttribute('data-color', color);
        btn.style.backgroundColor = color || '';
        btn.onclick = () => this.makeButtonSelected(btn);
        const container = document.getElementById('colorSelectContainer');
        container === null || container === void 0 ? void 0 : container.insertBefore(btn, container.querySelector('.color-block'));
        if (buttons.length === MAX_SAVED_COLORS + 1) {
            // remove the last button
            const lastBtn = container === null || container === void 0 ? void 0 : container.lastChild;
            lastBtn === null || lastBtn === void 0 ? void 0 : lastBtn.remove();
        }
    }
    setTextureAsBackground(texture) {
        if (!texture) {
            return;
        }
        new three_1.TextureLoader().load(texture, (texture) => {
            this.webgl.scene.background = texture;
            this.webgl.renderOnce();
        });
    }
    setColorAsBackground(color) {
        if (!color) {
            this.webgl.scene.background = null;
        }
        else {
            const hexColor = parseInt(color.substring(1), 16);
            this.webgl.scene.background = new three_1.Color(hexColor).convertLinearToSRGB();
            (0, pubsub_js_1.publish)("background-changed" /* BACKGROUND_CHANGED */, color);
        }
        this.webgl.renderOnce();
    }
}
exports.Background = Background;
